import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronRight, Gift } from 'lucide-react';
import { useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { ttStatsAPI } from '../services/api';
import AnimatedText from './AnimatedText';
import { EnhancedTournament } from '../types/api.types';
import { scrollToAppPromotion } from '../utils/scrollToApp';
import starTrophyUnlockData from '../assets/lottie/star_trophy_unlock.json';

interface PartneredTournament {
  id: string;
  date: string;
  tournament: {
    en: string;
    de: string;
    fr: string;
    it: string;
  };
  organizer: {
    en: string;
    de: string;
    fr: string;
    it: string;
  };
  organizerType: 'federation' | 'association' | 'club';
  description: {
    en: string;
    de: string;
    fr: string;
    it: string;
  };
  location?: {
    en: string;
    de: string;
    fr: string;
    it: string;
  };
  registrationLink?: string;
  organizerLink?: string;
  proCodesCount: number;
}

const partneredTournaments: PartneredTournament[] = [
  {
    id: 'kantonalmeisterschaft-2025',
    date: '2025-10-19',
    tournament: {
      en: 'Zurich Cantonal Championship',
      de: 'Zürcher Kantonalmeisterschaft',
      fr: 'Championnat Cantonal de Zurich',
      it: 'Campionato Cantonale di Zurigo'
    },
    organizer: {
      en: 'TTVKZ - Zurich Table Tennis Federation',
      de: 'TTVKZ - Tischtennisverband des Kantons Zürich',
      fr: 'TTVKZ - Fédération de Tennis de Table du Canton de Zurich',
      it: 'TTVKZ - Federazione di Ping Pong del Cantone di Zurigo'
    },
    organizerType: 'federation',
    description: {
      en: 'TTStats is the official partner of the Zurich Cantonal Championship organized by TTVKZ. October 18-19, 2025! Pro codes for all winners across all categories.',
      de: 'TTStats ist offizieller Partner der Zürcher Kantonalmeisterschaft vom TTVKZ. 18.-19. Oktober 2025! Pro Codes für alle Gewinner in allen Kategorien.',
      fr: 'TTStats est partenaire officiel du Championnat Cantonal de Zurich organisé par TTVKZ. 18-19 octobre 2025 ! Codes Pro pour tous les gagnants.',
      it: 'TTStats è partner ufficiale del Campionato Cantonale di Zurigo organizzato da TTVKZ. 18-19 ottobre 2025! Codici Pro per tutti i vincitori.'
    },
    location: {
      en: 'Zurich, Switzerland',
      de: 'Zürich, Schweiz',
      fr: 'Zurich, Suisse',
      it: 'Zurigo, Svizzera'
    },
    registrationLink: 'https://tt-turnier.ch/zkm',
    organizerLink: 'https://ttvkz.ch',
    proCodesCount: 16
  },
  {
    id: 'schmaetterball-cup-2025',
    date: '2025-11-09',
    tournament: {
      en: '54th Schmätterball-Cup',
      de: '54. Schmätterball-Cup',
      fr: '54ème Schmätterball-Cup',
      it: '54° Schmätterball-Cup'
    },
    organizer: {
      en: 'TTC Ettenhausen',
      de: 'TTC Ettenhausen',
      fr: 'TTC Ettenhausen',
      it: 'TTC Ettenhausen'
    },
    organizerType: 'club',
    description: {
      en: 'TTStats proudly supports the 54th Schmätterball-Cup. Pro subscriptions for singles and doubles winners in all categories A-D. November 9th, 2025.',
      de: 'TTStats unterstützt den 54. Schmätterball-Cup. Pro Abos für Einzel- und Doppel-Gewinner in allen Kategorien A-D. 9. November 2025.',
      fr: 'TTStats soutient le 54ème Schmätterball-Cup. Abos Pro pour les vainqueurs en simple et double, catégories A-D. 9 novembre 2025.',
      it: 'TTStats sostiene il 54° Schmätterball-Cup. Abbonamenti Pro per vincitori singolo e doppio, categorie A-D. 9 novembre 2025.'
    },
    location: {
      en: 'Ettenhausen, Thurgau',
      de: 'Ettenhausen, Thurgau',
      fr: 'Ettenhausen, Thurgovie',
      it: 'Ettenhausen, Turgovia'
    },
    registrationLink: 'https://tt-turnier.ch/ettenhausen',
    organizerLink: 'https://ttc-ettenhausen.ch/index.php/turnier/',
    proCodesCount: 32
  },
  {
    id: 'anjtt-2025',
    date: '2025-11-29',
    tournament: {
      en: 'ANJTT Individual Championships',
      de: 'ANJTT Einzelmeisterschaften',
      fr: 'Championnats Individuels ANJTT',
      it: 'Campionati Individuali ANJTT'
    },
    organizer: {
      en: 'ANJTT - Neuchâtel & Jura Table Tennis Association',
      de: 'ANJTT - Neuenburger & Jurassischer Tischtennisverband',
      fr: 'ANJTT - Association Neuchâteloise et Jurassienne de Tennis de Table',
      it: 'ANJTT - Associazione di Ping Pong di Neuchâtel e Giura'
    },
    organizerType: 'association',
    description: {
      en: 'TTStats supports the ANJTT Individual Championships. Pro codes for all age categories and series. November 29th, 2025 in Bienne.',
      de: 'TTStats unterstützt die ANJTT Einzelmeisterschaften. Pro Codes für alle Alterskategorien und Serien. 29. November 2025 in Biel.',
      fr: 'TTStats soutient les Championnats Individuels ANJTT. Codes Pro pour toutes catégories d\'âge et séries. 29 novembre 2025 à Bienne.',
      it: 'TTStats sostiene i Campionati Individuali ANJTT. Codici Pro per tutte le categorie. 29 novembre 2025 a Bienne.'
    },
    location: {
      en: 'Bienne, Switzerland',
      de: 'Biel, Schweiz',
      fr: 'Bienne, Suisse',
      it: 'Bienne, Svizzera'
    },
    registrationLink: 'https://tt-turnier.ch/anjtt/ANJTT_2025',
    organizerLink: 'https://anjtt.ch/',
    proCodesCount: 16
  }
];

const TournamentsSection: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const [activeFilter, setActiveFilter] = useState<'all' | 'open' | 'partners'>('open');
  const [tournaments, setTournaments] = useState<EnhancedTournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPastPartners, setShowPastPartners] = useState(false);
  const [playLottie, setPlayLottie] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const lottieInstanceRef = React.useRef<any>(null);

  const translations = {
    en: {
      title: 'Upcoming Events',
      subtitle: 'Tournaments & Championships • Swiss Table Tennis',
      featuredBadge: 'PARTNER',
      partnersOnly: 'Partners',
      viewFullList: 'View all events in app',
      noTournaments: 'No upcoming events',
      registrationOpen: 'Registration Open',
      registrationClosed: 'Registration Closed',
      allEvents: 'All Events',
      openRegistration: 'Open Registration',
      poweredBy: 'In collaboration with',
      proCodes: 'Pro',
      viewPast: 'View past partnerships',
      hidePast: 'Hide past partnerships',
      pastPartnerships: 'Past Partnerships'
    },
    fr: {
      title: 'Événements à Venir',
      subtitle: 'Tournois & Championnats • Tennis de Table Suisse',
      featuredBadge: 'PARTENAIRE',
      partnersOnly: 'Partenaires',
      viewFullList: 'Voir tous les événements dans l\'app',
      noTournaments: 'Aucun événement à venir',
      registrationOpen: 'Inscription Ouverte',
      registrationClosed: 'Inscription Fermée',
      allEvents: 'Tous les Événements',
      openRegistration: 'Inscriptions Ouvertes',
      poweredBy: 'En collaboration avec',
      proCodes: 'Pro',
      viewPast: 'Voir les partenariats passés',
      hidePast: 'Masquer les partenariats passés',
      pastPartnerships: 'Partenariats Passés'
    },
    de: {
      title: 'Kommende Events',
      subtitle: 'Turniere & Meisterschaften • Schweizer Tischtennis',
      featuredBadge: 'PARTNER',
      partnersOnly: 'Partner',
      viewFullList: 'Alle Events in der App ansehen',
      noTournaments: 'Keine kommenden Events',
      registrationOpen: 'Anmeldung Offen',
      registrationClosed: 'Anmeldung Geschlossen',
      allEvents: 'Alle Events',
      openRegistration: 'Offene Anmeldung',
      poweredBy: 'In Zusammenarbeit mit',
      proCodes: 'Pro',
      viewPast: 'Vergangene Partnerschaften anzeigen',
      hidePast: 'Vergangene Partnerschaften ausblenden',
      pastPartnerships: 'Vergangene Partnerschaften'
    },
    it: {
      title: 'Eventi in Arrivo',
      subtitle: 'Tornei & Campionati • Ping Pong Svizzero',
      featuredBadge: 'PARTNER',
      partnersOnly: 'Partner',
      viewFullList: 'Vedi tutti gli eventi nell\'app',
      noTournaments: 'Nessun evento in programma',
      registrationOpen: 'Iscrizioni Aperte',
      registrationClosed: 'Iscrizioni Chiuse',
      allEvents: 'Tutti gli Eventi',
      openRegistration: 'Iscrizioni Aperte',
      poweredBy: 'In collaborazione con',
      proCodes: 'Pro',
      viewPast: 'Vedi partnership passate',
      hidePast: 'Nascondi partnership passate',
      pastPartnerships: 'Partnership Passate'
    }
  };

  const t = translations[currentLang] || translations.en;

  useEffect(() => {
    const fetchTournaments = async () => {
      // If partners filter is active, don't fetch from API
      if (activeFilter === 'partners') {
        setLoading(false);
        setTournaments([]);
        return;
      }

      setLoading(true);
      try {
        const filterOpen = activeFilter === 'open';
        const data = await ttStatsAPI.getUpcomingTournaments(5, filterOpen);
        setTournaments(data);
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, [activeFilter]);

  // Intersection observer to play Lottie on view
  useEffect(() => {
    const currentRef = containerRef.current;

    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !playLottie) {
            setPlayLottie(true);
            // Trigger play on the Lottie instance
            if (lottieInstanceRef.current) {
              lottieInstanceRef.current.play();
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    observer.observe(currentRef);

    return () => {
      observer.disconnect();
    };
  }, [playLottie]);

  const formatDate = (timestamp: number | string | null): { day: number; month: string } => {
    if (!timestamp) return { day: 0, month: '' };

    let date: Date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    const day = date.getDate();
    const month = date.toLocaleDateString(currentLang, { month: 'short' });
    return { day, month };
  };

  const formatDateString = (timestamp: number | null): string => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const isRegistrationOpen = (tournament: EnhancedTournament): boolean => {
    const now = Date.now();
    const regEndTime = tournament.options.reg_end_time;

    if (!regEndTime || tournament.options.block_enrollment) return false;

    const regEndTimestamp = parseInt(regEndTime);
    if (isNaN(regEndTimestamp)) return false;

    return now < regEndTimestamp;
  };

  const getTournamentUrl = (tournament: EnhancedTournament): string => {
    if (tournament.options.enrollment_link) {
      return tournament.options.enrollment_link;
    }
    return `https://tt-turnier.ch/en/${tournament.id}`;
  };

  const isPast = (dateString: string) => {
    const tournamentDate = new Date(dateString);
    tournamentDate.setHours(23, 59, 59, 999);
    const now = new Date();
    return now > tournamentDate;
  };

  const upcomingPartnered = partneredTournaments.filter(item => !isPast(item.date));
  const pastPartnered = partneredTournaments.filter(item => isPast(item.date));

  const filters = [
    { id: 'open', label: t.openRegistration },
    { id: 'all', label: t.allEvents },
    { id: 'partners', label: t.partnersOnly }
  ];

  // Check if a tournament is partnered by matching registration link
  const getPartneredInfo = (tournament: EnhancedTournament) => {
    const url = getTournamentUrl(tournament);
    // Check if any partnered tournament link matches
    return upcomingPartnered.find(partnered => {
      const link = partnered.registrationLink;
      if (link) {
        const linkPart = link.split('/').pop() || '';
        return linkPart && url.includes(linkPart);
      }
      return false;
    }) || null;
  };

  return (
    <section className="py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4">
        {/* Main Title */}
        <div className="text-center mb-10">
          {/* Lottie above title */}
          <motion.div
            ref={containerRef}
            className="flex justify-center mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div style={{ width: '200px', height: '200px' }}>
              <Lottie
                lottieRef={lottieInstanceRef}
                animationData={starTrophyUnlockData}
                loop={false}
                autoplay={false}
                style={{ width: '100%', height: '100%' }}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid meet'
                }}
              />
            </div>
          </motion.div>
          <AnimatedText
            text={t.title}
            className="text-4xl font-light text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300"
            delay={0.1}
            staggerDelay={0.02}
          />
          <motion.div
            className="flex flex-col items-center gap-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}>
            <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
              {t.subtitle}
            </p>
            <a
              href="https://tt-turnier.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex items-center gap-1">
              {t.poweredBy} <span className="font-medium">tt-turnier.ch</span>
            </a>
          </motion.div>
        </div>

        {/* Filter Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-800 p-1 transition-colors duration-300">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id as any)}
                className={`
                  relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${activeFilter === filter.id
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}
                `}>
                <span className="relative z-10">
                  {filter.label}
                </span>
                {activeFilter === filter.id && (
                  <motion.div
                    className="absolute inset-0 bg-white dark:bg-gray-700 rounded-full shadow-sm transition-colors duration-300"
                    layoutId="activeFilter"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* All Tournaments Content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="max-w-3xl mx-auto">
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div
                    key={item}
                    className="flex items-center justify-between gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-transparent transition-colors duration-300">
                    {/* Icon skeleton */}
                    <div className="relative w-8 h-8 flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse transition-colors duration-300" />
                    </div>

                    {/* Content skeleton */}
                    <div className="flex-1 min-w-0">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-md mb-2 animate-pulse transition-colors duration-300" style={{ width: `${150 + item * 30}px` }} />
                      <div className="h-3 bg-gray-100 dark:bg-gray-600 rounded-md animate-pulse transition-colors duration-300" style={{ width: `${100 + item * 20}px` }} />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ) : activeFilter === 'partners' ? (
            // Show only partnered tournaments
            upcomingPartnered.length === 0 ? (
              <motion.div
                key="empty-partners"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12">
                <p className="text-gray-500">{t.noTournaments}</p>
              </motion.div>
            ) : (
              <motion.div
                key="partners"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="max-w-3xl mx-auto">
                <div className="space-y-2">
                  {upcomingPartnered.map((item, index) => {
                    const { day, month } = formatDate(item.date);
                    return (
                      <motion.a
                        key={item.id}
                        href={item.registrationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="flex items-center justify-between gap-3 p-4 rounded-xl hover:shadow-md transition-all cursor-pointer group relative bg-gradient-to-r from-amber-50/50 to-white dark:from-amber-900/20 dark:to-gray-800 border-2 border-amber-200/60 dark:border-amber-700/40">

                        {/* Featured ribbon */}
                        <motion.div
                          className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + index * 0.05, type: "spring" }}
                        >
                          <Gift className="w-2.5 h-2.5" />
                          {t.featuredBadge}
                        </motion.div>

                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="relative w-8 h-8 flex-shrink-0">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600">
                              <Calendar className="h-4 w-4 text-white" />
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <AnimatedText
                              text={item.tournament[currentLang]}
                              className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-1"
                              delay={0.1 + index * 0.05}
                              staggerDelay={0.01}
                            />
                            <motion.div
                              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.15 + index * 0.05 }}>
                              <span className="whitespace-nowrap">{`${day} ${month}`}</span>
                              {item.location && (
                                <>
                                  <span className="hidden sm:inline">•</span>
                                  <span className="truncate">{item.location[currentLang]}</span>
                                </>
                              )}
                            </motion.div>
                          </div>
                        </div>

                        <motion.div
                          className="flex-shrink-0"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.25 + index * 0.05 }}
                        >
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500 transition-colors duration-300">
                              <Gift className="w-4 h-4" />
                              <span className="text-lg font-bold">{item.proCodesCount}×</span>
                            </div>
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium transition-colors duration-300">{t.proCodes}</div>
                          </div>
                        </motion.div>
                      </motion.a>
                    );
                  })}
                </div>

                {/* Past Partnerships Toggle */}
                {pastPartnered.length > 0 && (
                  <div className="mt-8">
                    <motion.button
                      onClick={() => setShowPastPartners(!showPastPartners)}
                      className="w-full text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition mb-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <div className="inline-flex items-center gap-2">
                        {showPastPartners ? t.hidePast : t.viewPast}
                        <ChevronRight className={`h-4 w-4 transition-transform ${showPastPartners ? 'rotate-90' : ''}`} />
                      </div>
                    </motion.button>

                    {showPastPartners && (
                      <motion.div
                        className="space-y-2"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {pastPartnered.map((item, index) => {
                          const { day, month } = formatDate(item.date);
                          return (
                            <motion.a
                              key={item.id}
                              href={item.registrationLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                              className="flex items-center justify-between gap-3 p-4 rounded-xl hover:shadow-md transition-all cursor-pointer group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 opacity-60">

                              {/* Featured ribbon */}
                              <motion.div
                                className="absolute -top-2 -right-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 + index * 0.05, type: "spring" }}
                              >
                                <Gift className="w-2.5 h-2.5" />
                                {t.featuredBadge}
                              </motion.div>

                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div className="relative w-8 h-8 flex-shrink-0">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-500">
                                    <Calendar className="h-4 w-4 text-white" />
                                  </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                  <AnimatedText
                                    text={item.tournament[currentLang]}
                                    className="font-medium text-gray-700 dark:text-gray-300 transition-colors mb-1"
                                    delay={0.1 + index * 0.05}
                                    staggerDelay={0.01}
                                  />
                                  <motion.div
                                    className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300"
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15 + index * 0.05 }}>
                                    <span className="whitespace-nowrap">{`${day} ${month}`}</span>
                                    {item.location && (
                                      <>
                                        <span className="hidden sm:inline">•</span>
                                        <span className="truncate">{item.location[currentLang]}</span>
                                      </>
                                    )}
                                  </motion.div>
                                </div>
                              </div>

                              <motion.div
                                className="flex-shrink-0"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.25 + index * 0.05 }}
                              >
                                <div className="text-right">
                                  <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500 transition-colors duration-300">
                                    <Gift className="w-4 h-4" />
                                    <span className="text-lg font-bold">{item.proCodesCount}×</span>
                                  </div>
                                  <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium transition-colors duration-300">{t.proCodes}</div>
                                </div>
                              </motion.div>
                            </motion.a>
                          );
                        })}
                      </motion.div>
                    )}
                  </div>
                )}
              </motion.div>
            )
          ) : tournaments.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12">
              <p className="text-gray-500">{t.noTournaments}</p>
            </motion.div>
          ) : (
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-3xl mx-auto">
              <div className="space-y-2">
                {tournaments.map((tournament, index) => {
                  const startDate = formatDateString(tournament.options.planned_start_time);
                  const location = tournament.options.organizer_city || tournament.options.organizer_name;
                  const regOpen = isRegistrationOpen(tournament);
                  const partneredInfo = getPartneredInfo(tournament);
                  const isFeatured = partneredInfo !== null;

                  return (
                    <motion.a
                      key={tournament.id}
                      href={getTournamentUrl(tournament)}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`flex items-center justify-between gap-3 p-4 rounded-xl hover:shadow-md transition-all cursor-pointer group relative ${
                        isFeatured
                          ? 'bg-gradient-to-r from-amber-50/50 to-white dark:from-amber-900/20 dark:to-gray-800 border-2 border-amber-200/60 dark:border-amber-700/40'
                          : 'bg-white dark:bg-gray-800 border border-transparent dark:border-transparent'
                      }`}>

                      {/* Featured ribbon */}
                      {isFeatured && (
                        <motion.div
                          className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + index * 0.05, type: "spring" }}
                        >
                          <Gift className="w-2.5 h-2.5" />
                          {t.featuredBadge}
                        </motion.div>
                      )}

                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {/* Event Icon with registration indicator */}
                        <div className="relative w-8 h-8 flex-shrink-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isFeatured
                              ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                              : 'bg-[#1C7AD6]'
                          }`}>
                            <Calendar className="h-4 w-4 text-white" />
                          </div>
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2 + index * 0.05, type: "spring", stiffness: 300, damping: 20 }}
                            className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                              regOpen ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ${
                              isFeatured ? 'text-gray-900 dark:text-gray-100' : 'text-gray-900 dark:text-gray-100'
                            }`} style={{ textWrap: 'balance' } as React.CSSProperties}>
                              {tournament.name}
                            </h3>
                          </div>
                          <motion.div
                            className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 + index * 0.05 }}>
                            {startDate && <span className="whitespace-nowrap">{startDate}</span>}
                            {location && (
                              <>
                                <span className="hidden sm:inline">•</span>
                                <span className="truncate">{location}</span>
                              </>
                            )}
                          </motion.div>
                        </div>
                      </div>

                      {/* Pro Prize Badge for featured tournaments */}
                      {isFeatured && partneredInfo && (
                        <motion.div
                          className="flex-shrink-0"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.25 + index * 0.05 }}
                        >
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500 transition-colors duration-300">
                              <Gift className="w-4 h-4" />
                              <span className="text-lg font-bold">{partneredInfo.proCodesCount}×</span>
                            </div>
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium transition-colors duration-300">{t.proCodes}</div>
                          </div>
                        </motion.div>
                      )}
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Legend and View All Link */}
        {!loading && tournaments.length > 0 && (
          <motion.div
            className="max-w-3xl mx-auto mt-6 space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}>
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full" />
                <span>{t.registrationOpen}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                <span>{t.registrationClosed}</span>
              </div>
            </div>

            {/* View All Link */}
            <div className="text-center">
              <button
                onClick={scrollToAppPromotion}
                className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition">
                {t.viewFullList}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default TournamentsSection;
